import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';

import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { DnDFiles, makeFiles } from '@dynamika-shared/ui/DnDFiles/DnDFiles';
import { AlertIcon, UploadBoxIcon } from '@dynamika-shared/ui/icons';

import { twJoin } from '@utils/twMerge';
import { subscribeToActions } from '@restore/store';
import {
  uploadBulkRecipientsFailure,
  uploadBulkRecipientsRejected,
  uploadBulkRecipientsSuccess,
  useClinicalTrialSiteRecipientsSlice,
} from '@restore/clinicalTrialSiteRecipients/clinicalTrialSiteRecipients.slice';
import {
  buildUploadSummary,
  type UploadSummary,
} from '@restore/clinicalTrialSiteRecipients/clinicalTrialSiteRecipients.utils';
import { UploadRecipientsExpectedColumns } from './UploadRecipientsExpectedColumns';
import { UploadRecipientsFileCard } from './UploadRecipientsFileCard';
import { UploadRecipientsSummary } from './UploadRecipientsSummary';

export type UploadRecipientsDrawerProps = {
  open: boolean;
  close: () => void;
  clinicalTrialId: string;
  onUploaded: () => void;
};

const FORMAT_ERROR_MESSAGE = 'Please select a CSV file';

const isCsv = (file: File) =>
  file.name.split('.').pop()?.toLowerCase() === 'csv';

/**
 * One CSV per submission; only a non-CSV is refused up front, row rules stay
 * server-side. Two screens: the picker, then the summary (D7-7650).
 */
export const UploadRecipientsDrawer: FC<UploadRecipientsDrawerProps> = ({
  open,
  close,
  clinicalTrialId,
  onUploaded,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [formatError, setFormatError] = useState(false);
  const [summary, setSummary] = useState<UploadSummary | null>(null);

  /**
   * Latched on the first reported run — `summary` clears on every fresh pick,
   * the rows it saved do not. `handleClose` refreshes the list off this.
   */
  const [hasReportedRun, setHasReportedRun] = useState(false);

  const {
    uploading,
    templateDownloading,
    uploadBulkRecipients,
    downloadBulkTemplate,
  } = useClinicalTrialSiteRecipientsSlice((state, actions) => ({
    uploading: state.uploading,
    templateDownloading: state.templateDownloading,
    uploadBulkRecipients: actions.uploadBulkRecipients,
    downloadBulkTemplate: actions.downloadBulkTemplate,
  }));

  const resetState = useCallback(() => {
    setFile(null);
    setFormatError(false);
    setSummary(null);
    setHasReportedRun(false);
  }, []);

  /**
   * Cleared on the way in, never on the way out — Ant keeps the panel mounted
   * through the slide-out, so resetting at close flashes the empty state back
   * on screen before it disappears (D7-7710).
   */
  useEffect(() => {
    if (open) resetState();
  }, [open, resetState]);

  /**
   * Rows are saved once a run reports, so dismissal refreshes — guarded so
   * cancelling the picker without sending anything does not refetch.
   */
  const handleClose = useCallback(() => {
    if (hasReportedRun) onUploaded();

    close();
  }, [hasReportedRun, onUploaded, close]);

  /**
   * Both outcomes report rather than close — auto-closing would discard the
   * row numbers. A thrown request is not a completion; the file stays put.
   */
  useEffect(() => {
    if (!open) return;

    const showSummary = (result?: Parameters<typeof buildUploadSummary>[0]) => {
      setFormatError(false);
      setSummary(buildUploadSummary(result));
      setHasReportedRun(true);
    };

    const unsubscribeSuccess = subscribeToActions(
      uploadBulkRecipientsSuccess,
      action => showSummary(action.payload)
    );

    const unsubscribeRejected = subscribeToActions(
      uploadBulkRecipientsRejected,
      action => showSummary(action.payload)
    );

    const unsubscribeFailure = subscribeToActions(
      uploadBulkRecipientsFailure,
      () => {
        setFormatError(false);
        setSummary(null);
      }
    );

    return () => {
      unsubscribeSuccess();
      unsubscribeRejected();
      unsubscribeFailure();
    };
  }, [open]);

  /**
   * One file per upload. A rejected pick clears the attachment too, so the
   * warning and the disabled submit always describe the same state.
   */
  const addFiles = useCallback((files: File[]) => {
    const [selected] = files;

    if (!selected) return;

    // A new attempt starts clean — the previous run's row numbers no longer
    // describe what is attached.
    setSummary(null);

    if (!isCsv(selected)) {
      setFormatError(true);
      setFile(null);

      return;
    }

    setFormatError(false);
    setFile(selected);
  }, []);

  const handleFileInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        addFiles(makeFiles(event.target.files));
        // Lets the same file be re-picked after being cleared.
        event.target.value = '';
      }
    },
    [addFiles]
  );

  const handleDownloadTemplate = () => {
    downloadBulkTemplate({ clinicalTrialId });
  };

  /**
   * The file has left the picker — show Validation Results from here on.
   * True while the request is in flight and again once it has answered.
   */
  const processing = uploading || Boolean(summary);

  /**
   * Confirm sends the file, then becomes the way out. Routed through
   * `handleClose` so dismissal is shared with Cancel and the X.
   */
  const handleConfirm = () => {
    if (summary) {
      handleClose();

      return;
    }

    if (!file) return;

    setSummary(null);

    uploadBulkRecipients({ clinicalTrialId, file });
  };

  /**
   * Confirm uploads before a run answers and dismisses after, so a partly
   * refused file still closes (D7-7709) — refusals are reported, not locked.
   */
  const confirmDisabled = !summary && (!file || uploading);

  /**
     * The reported file steps aside so the next one can go up without reopening.
   */
  const attachedFile = summary ? null : file;

  return (
    <Drawer
      data-testid='upload-recipients-drawer'
      classNames={{
        header:
          '!px-0 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        /**
         * Ant's body scrolls by default, taking the footer with it — hidden
         * here so the column below scrolls and the buttons hold the bottom.
         */
        body: 'flex flex-col !overflow-hidden !pl-0 !pr-0',
        content: 'rounded-lg',
      }}
      title={'Upload'}
      open={open}
      width={464}
      placement='right'
      onClose={handleClose}
      closeIcon={<CloseOutlined data-testid='close-upload-recipients-drawer' />}
    >
      <div className='min-h-0 flex-1 overflow-y-auto'>
        {/* Clearing the card brings the drop zone back, and so does a
            finished run. */}
        {attachedFile ? (
          <UploadRecipientsFileCard
            file={attachedFile}
            onRemove={processing ? undefined : () => setFile(null)}
          />
        ) : (
          <>
            {formatError && (
              <div
                data-testid='upload-recipients-alert'
                className='mb-2 w-full rounded-2xl border-[1px] border-redOrange bg-seashell px-2 py-1.5'
              >
                <p className='flex items-center'>
                  <AlertIcon className='mr-1 h-2 w-2 text-redOrange' />
                  {FORMAT_ERROR_MESSAGE}
                </p>
              </div>
            )}

            <div className='mb-2'>
              <DnDFiles onFilesAdded={addFiles}>
                {isDragActive => (
                  <label
                    className={twJoin(
                      'flex cursor-pointer flex-col items-center justify-center rounded-sm border border-pastelBlue bg-white transition duration-300 hover:bg-waterLight hover:bg-opacity-50',
                      // Compact once results share the panel below.
                      hasReportedRun ? 'h-[8.8rem]' : 'h-[19.2rem]',
                      isDragActive && 'border-vividTangelo opacity-30',
                      formatError && 'border-solid border-vermilion',
                      (!formatError || isDragActive) && 'border-dashed'
                    )}
                  >
                    <div className='flex flex-col items-center'>
                      <div
                        className={twJoin(
                          'h-max rounded-full border-2 border-vividTangelo',
                          hasReportedRun ? 'mb-1 p-1' : 'mb-2 p-2'
                        )}
                      >
                        <UploadBoxIcon
                          className={twJoin(
                            'text-vividTangelo',
                            hasReportedRun ? 'h-2 w-2' : 'h-4 w-4'
                          )}
                        />
                      </div>

                      <div
                        className={twJoin(
                          'flex flex-col items-center font-medium',
                          hasReportedRun ? 'text-1216' : 'text-1624'
                        )}
                      >
                        <span className='text-chineseBlack'>
                          Upload a file in{' '}
                          <span className='text-spanishGray'>.csv</span>
                        </span>
                        <span className='text-chineseBlack'>
                          or select{' '}
                          <span className='text-vividTangelo'>
                            click to browse
                          </span>
                        </span>

                        <input
                          data-testid='upload-recipients-input'
                          className='hidden'
                          type='file'
                          accept='.csv'
                          onChange={handleFileInput}
                        />
                      </div>
                    </div>
                  </label>
                )}
              </DnDFiles>
            </div>
          </>
        )}

        {processing && <UploadRecipientsSummary summary={summary} />}

        {/* Tied to the drop zone — a file already in hand needs no template. */}
        {!attachedFile && (
          <div className={twJoin(processing && 'mt-2')}>
            <UploadRecipientsExpectedColumns
              defaultOpen={!hasReportedRun}
              downloading={templateDownloading}
              onDownloadTemplate={handleDownloadTemplate}
            />
          </div>
        )}
      </div>

      <footer className='mt-auto shrink-0 bg-white pt-3'>
        <hr className='mb-3' />

        <div className='flex justify-between'>
          <Button
            data-testid='upload-recipients-cancel'
            className='w-[17rem]'
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            data-testid='upload-recipients-submit'
            className='w-[17rem]'
            type='primary'
            disabled={confirmDisabled}
            loading={uploading}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      </footer>
    </Drawer>
  );
};
